import React, { useEffect } from 'react';
import Banner from '../components/common/Banner';
import SectionTitle from '../components/common/SectionTitle';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import emailjs from 'emailjs-com';

const ContactPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Contact Us - Vet Explore Hub';
    emailjs.init("YOUR_USER_ID");
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    
    try {
      await emailjs.sendForm(
        'YOUR_SERVICE_ID',
        'YOUR_TEMPLATE_ID',
        form as unknown as HTMLFormElement
      );
      
      alert('Message sent successfully!');
      form.reset();
    } catch (error) {
      console.error('Email error:', error);
      alert('Failed to send message. Please try again.');
    }
  };

  return (
    <>
      <Banner 
        title="Contact Us"
        subtitle="Get in touch with our team for any queries about BVSc admissions"
        backgroundImage="https://images.pexels.com/photos/1181244/pexels-photo-1181244.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
      />
      
      <section className="section">
        <div className="container-custom">
          <SectionTitle 
            title="Get In Touch" 
            subtitle="We're here to answer any questions you have about veterinary education and admissions."
          />
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
                <h3 className="text-2xl font-semibold mb-6 text-primary-700">Send Us a Message</h3>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-gray-700 font-medium mb-1">
                        Full Name*
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="form-input"
                        placeholder="Enter your full name"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="email" className="block text-gray-700 font-medium mb-1">
                        Email Address*
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="form-input"
                        placeholder="Enter your email"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-gray-700 font-medium mb-1">
                        Phone Number*
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        required
                        className="form-input"
                        placeholder="Enter your phone number"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="subject" className="block text-gray-700 font-medium mb-1">
                        Subject*
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        required
                        className="form-input"
                        placeholder="What is this regarding?"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-gray-700 font-medium mb-1">
                      Message*
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={6}
                      className="form-input"
                      placeholder="How can we help you?"
                    ></textarea>
                  </div>
                  
                  <div>
                    <button
                      type="submit"
                      className="btn btn-primary w-full md:w-auto"
                    >
                      Send Message
                    </button>
                  </div>
                </form>
              </div>
            </div>
            
            <div>
              <div className="bg-white rounded-lg shadow-md p-6 md:p-8 mb-8">
                <h3 className="text-xl font-semibold mb-6 text-primary-700">Contact Information</h3>
                
                <ul className="space-y-6">
                  <li className="flex items-start">
                    <Phone size={20} className="text-primary-500 mr-3 mt-1" />
                    <div>
                      <p className="font-medium text-gray-800">Phone</p>
                      <a href="tel:+918452055440" className="text-gray-600 hover:text-primary-600">
                        +91 8452 055 440
                      </a>
                    </div>
                  </li>
                  
                  <li className="flex items-start">
                    <Mail size={20} className="text-primary-500 mr-3 mt-1" />
                    <div>
                      <p className="font-medium text-gray-800">Email</p>
                      <a href="mailto:admissionsveterinary@gmail.com" className="text-gray-600 hover:text-primary-600">
                        admissionsveterinary@gmail.com
                      </a>
                    </div>
                  </li>
                  
                  <li className="flex items-start">
                    <MapPin size={20} className="text-primary-500 mr-3 mt-1" />
                    <div>
                      <p className="font-medium text-gray-800">Address</p>
                      <p className="text-gray-600">
                        BDA Complex, HSR Layout, Bengaluru, India
                      </p>
                    </div>
                  </li>
                  
                  <li className="flex items-start">
                    <Clock size={20} className="text-primary-500 mr-3 mt-1" />
                    <div>
                      <p className="font-medium text-gray-800">Working Hours</p>
                      <p className="text-gray-600">
                        Monday - Saturday: 9:00 AM - 6:00 PM<br />
                        Sunday: Closed
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
              
              <div className="bg-primary-600 rounded-lg shadow-md p-6 md:p-8 text-white">
                <h3 className="text-xl font-semibold mb-4">Need Urgent Assistance?</h3>
                <p className="mb-6">
                  Contact our helpline for immediate assistance with your queries about BVSc admissions.
                </p>
                <a 
                  href="tel:+918452055440" 
                  className="btn bg-white text-primary-700 hover:bg-gray-100 w-full flex items-center justify-center"
                >
                  <Phone size={18} className="mr-2" />
                  Call Our Helpline
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <section className="py-12">
        <div className="container-custom">
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3888.599371459659!2d77.63399631482233!3d12.914482090894431!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae1481c7dd1c3b%3A0x7c20abd0c8b0f79c!2sHSR%20Layout%2C%20Bengaluru%2C%20Karnataka!5e0!3m2!1sen!2sin!4v1629789456789!5m2!1sen!2sin" 
              width="100%" 
              height="450" 
              style={{ border: 0 }} 
              allowFullScreen={true} 
              loading="lazy" 
              referrerPolicy="no-referrer-when-downgrade"
              title="Vet Explore Hub Location"
            ></iframe>
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactPage;